import { Component, OnInit, AfterViewInit, OnDestroy, Input, ChangeDetectorRef } from '@angular/core';
import { NewRelicService } from '../../../../../../../angular-app/projects/brokerage-eapp/src/app/services/newrelic.service';
import { FormGroup, FormArray, ControlContainer } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { first, debounceTime, takeUntil } from 'rxjs/operators';

import { AppState } from '../../app-reducer';
import { ProcessorService } from '../../services/processor.service';
import { NavigationService } from '../../services/navigation.service';
import { QuestionControlService } from '../../services/question-control.service';
import { clone, cloneDeep, filter, find, forEach, get, isArray, isEmpty, isEqual } from 'lodash';

@Component({
  selector: 'medical-conditions',
  templateUrl: './medical-conditions.component.html'
})
export class MedicalConditionsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() parent: FormGroup;
  @Input() childQuestions: any;
  @Input() formGroupName: any;

  public medicalConditionsFormGroup: FormGroup;
  public parentFormGroup: FormGroup;
  public medicalConditionsArray: FormArray;
  
  appState$: Observable<any>;
  questions: any;
  conditionsList = [];
  defaultValuesChecked: string[] = [];
  prefilled: boolean;
  gender: string;
  sectionBlock: string;
  questionsAll: any;
  formVal: any;
  visibleQuestions: any;
  sectionMenuData: any;
  private _onDestroy = new Subject();
  private appId: string;

  constructor(
    private newRelicService: NewRelicService,
    private controlContainer: ControlContainer,
    private processorService: ProcessorService,
    private navigationService: NavigationService,
    private qcs: QuestionControlService,
    public route: ActivatedRoute,
    private store: Store<AppState>,
    private changeDetector: ChangeDetectorRef
    ) {
      this.appState$ = this.store.select('sections');
      this.sectionBlock = this.route.snapshot.data.blockName;
      // get questions from state
      this.appState$.pipe(
        first()
      ).subscribe((state: any) => {
        this.appId = state.appId;
        if (state.questions) {
          this.questions = find(state.questions, { Name: this.sectionBlock }).Questions;
        }

        this.gender = state.content.SECTIONAProposedInsured ? state.content.SECTIONAProposedInsured.Gender : 'Male';

        const applicationContent = get(state, 'content') || {};
        this.prefilled = !isEmpty(applicationContent[this.sectionBlock]) ? true : false;

        this.questionsAll = state.questions;

        this.sectionMenuData = find(state.content.menuData, { name: this.sectionBlock });

      });
      
    }

  ngOnInit() {
    this.newRelicService.addPageAction('ngOnInit Start', { status: 'started', appId: this.appId });

    this.medicalConditionsFormGroup = <FormGroup>this.controlContainer.control;
    this.parentFormGroup = <FormGroup>this.parent;

    this.sectionBlock = this.route.snapshot.data.blockName;
    this.formVal = cloneDeep(this.medicalConditionsFormGroup.value);
    this.visibleQuestions = cloneDeep(this.medicalConditionsFormGroup.value);

    this.newRelicService.addPageAction('ngOnInit End', { status: 'completed', appId: this.appId });
  }

  ngAfterViewInit() {
    this.newRelicService.addPageAction('ngAfterViewInit Start', { status: 'started', appId: this.appId });

    // Updates left nav status indicator based on parent form validity
    this.parentFormGroup.statusChanges.pipe(takeUntil(this._onDestroy),debounceTime(100)).subscribe(status => {
      if (this.sectionMenuData.valid && status !== 'VALID' || !this.sectionMenuData.valid && status === 'VALID') {
        this.sectionMenuData.valid = status === 'VALID' ? true : null;
        this.navigationService.updateNav(this.parentFormGroup.controls, this.sectionBlock, this.visibleQuestions);
        this.changeDetector.markForCheck();
      }
    });

    // Watches the form for changes to questions, then performs ConditionalClause analysys on questions listed in ConditionalDependents
    // also used to nullify the question's key in visibleQuestions if it has a ConditionalClause so it gets rechecked on the next change detection cycle
    this.medicalConditionsFormGroup.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(x => {
      if (isEqual(this.formVal, x)) {
        return;
      } else if (this.formVal) {
        const diff = Object.keys(x).filter(k => x[k] != this.formVal[k]);
        if (diff.length > 0) {
          // console.log(diff, 'changed');
          diff.forEach(changedQuestion => {
            const question = this.qcs.getQuestion(this.questions, changedQuestion);
            // prevents analysing conditionality of questions that have data entered but aren't valid (stops repeated unecessary checks)
            if (this.medicalConditionsFormGroup.controls[changedQuestion.toString().split('.').pop()].dirty && !this.medicalConditionsFormGroup.controls[changedQuestion.toString().split('.').pop()].valid) {
              return;
            }
            if (question && question.ConditionalClause) {
              this.visibleQuestions[changedQuestion] = null;
            }
            if (question && question.ConditionalDependents) {
              this.checkConditionalDependents(question);
            }
          });
        }
      }
      this.formVal = x;
      this.newRelicService.addPageAction('Form Value Changed', { appId: this.appId, formValue: x });
    });

    this.newRelicService.addPageAction('ngAfterViewInit End', { status: 'completed', appId: this.appId });
  }

  ngOnDestroy() {
    this.newRelicService.addPageAction('ngOnDestroy Start', { status: 'started', appId: this.appId });

    this._onDestroy.next();
    this.formVal = null;

    this.newRelicService.addPageAction('ngOnDestroy End', { status: 'completed', appId: this.appId });
  }

  isQuestionVisible(question) {
    if (this.visibleQuestions[question.QuestionName] === null || (isArray(this.visibleQuestions[question.QuestionName]) && this.visibleQuestions[question.QuestionName].every(x => x !== true))) {
      this.showConditionalQuestion(question);
    }
    // work-around for forced reflow issue that resets formControls but not visibleQuestions, resulting in incorrect invalids
    if (!this.visibleQuestions[question.QuestionName]) {
      this.medicalConditionsFormGroup.controls[question.QuestionName].disable();
    }
    return this.visibleQuestions[question.QuestionName];
  }

  checkConditionalDependents(question) {
    const dependentQuestions = question.ConditionalDependents;
    dependentQuestions.forEach(q => {
      if (q.toString().toLowerCase().includes(this.sectionBlock.toLowerCase() + '.')) {
        q = q.toString().split('.').pop();
        this.visibleQuestions[q] = null;
      } else {
        const cue = q.toString().split('.');
        const section: any = find(this.questionsAll, { Name: cue[0] });
        this.appState$.pipe(first()).subscribe((state: any) => {
          const menuData = state.content.menuData;
          const offBlock = find(menuData, { name: cue[0] });
          if (typeof offBlock === 'undefined' || isEmpty(offBlock.questions)) {
          } else {
            const offBlockQ = find(offBlock.questions, { name: cue[1] });
            const question = this.qcs.getQuestion(section.Questions, cue[1]);
            let showNow: boolean;
            const ancestors = this.getLineage(question, section.Questions, []);
            for (let i = 0; i < ancestors.length; i++) {
              if (!ancestors[i].ConditionalClause) {
                showNow = true;
              } else {
                showNow = this.processorService.showConditionalQuestion(ancestors[i], section.Questions, this.medicalConditionsFormGroup.value);
              }
              if (!showNow) {
                break;
              }
            };
            if (!offBlockQ.visible) {
              if (showNow) {
                offBlockQ.visible = true;
                offBlockQ.valid = false;
              } else {
                offBlockQ.visible = false;
                offBlockQ.valid = null;
              }
            } else {
              offBlockQ.visible = showNow;
              offBlockQ.valid = this.checkOffBlockQuestionValidity(question, section.Name);
            }
          }
        });
      }
    });
  }

  checkOffBlockQuestionValidity(question, sectionName) {
    let result: boolean;
    this.appState$.pipe(first()).subscribe((state: any) => {
      if (state.content[sectionName]) {
        const val = cloneDeep(state.content[sectionName][question.QuestionName]);
        if (question.QuestionOptions.length > 0) {
          const selectedOption = question.QuestionOptions.find(item => item.key === val);
          if (selectedOption) {
            if (selectedOption.ConditionalClause) {
              const stillGood = this.processorService.showConditionalOption(selectedOption.ConditionalClause, this.medicalConditionsFormGroup.value);
              if (stillGood === false) {
                state.content[sectionName][question.QuestionName] = null;
                result = false;
              } else {
                result = true;
              }
            } else {
              result = true;
            }
          }
        } else {
          if (question.validators.length === 0) {
            result = true;
          } else {
            result = true;
          }
        }
      } else {
        result = null
      }
    });
    return result;
  }

  getChildQuestions(question: any) {
    return this.qcs.getChildQuestions(question, this.questions, true);
  }

  get drawerTitle() {
    let conditionName = clone(this.questions);
    conditionName = filter(conditionName, { QuestionName: 'ConditionName' });
    const name = filter(conditionName[0].QuestionOptions, { Name: this.medicalConditionsFormGroup.get('ConditionName').value });
    let nameVal: string;
    if (name[0].QuestionOptionVariations) {
      name[0].QuestionOptionVariations.forEach((variation) => {
        if (this.processorService.analyzeConditionalArray(variation.ConditionalClause, this.questions, this.parent.value, this.medicalConditionsFormGroup.value)) {
          nameVal = variation.AlternativeQuestionOptionText ? variation.AlternativeQuestionOptionText : " ";
        } else {
          nameVal = get(name[0], 'DisplayName');
        }
      });
    } else {
      nameVal = get(name[0], 'DisplayName');
    }
    return nameVal;
  }

  getLineage(object, questions, array) {
    array.push(object);
    const {ParentQuestionName} = object;
    if (ParentQuestionName) {
      this.questions.forEach(question => {
        if (question.QuestionName === ParentQuestionName) {
          this.getLineage(question, questions, array);
        }
      })
    }
    return array;
  }

  /**
   * Check ConditionalClause then ParentQuestion ConditionalClause if available.
   * @param question - question object
   * @returns boolean
   */
   showConditionalQuestion(question, parentShown:boolean = true, offBlockQuestions?) {
    const { QuestionName, ConditionalClause } = question;

    // adding default values for diagnosis questions, removing conditionality that suppresses them
    // and adding a value to the question to hide them and ConditionName in css on the form, so they
    // will be submitted to JHADE, configured to only run once
    if ((question.DefaultValue || QuestionName === 'ConditionName') && !this.defaultValuesChecked.includes(QuestionName)) {
      if (QuestionName === 'ConditionName') {
        question.hide = true;
      }
      // reformats intentionally suppressed questions' ConditionalClauses to inject DefaultValues
      if (ConditionalClause && ConditionalClause[ConditionalClause.length - 1] === 'FALSE') {
        question.hide = true;
        ConditionalClause.splice(ConditionalClause.length - 2, 2);
      }
      if (QuestionName ==='ConditionDiagnosisDepression') {
        if (this.gender === 'Male') {
          question.hide = true;
          if (ConditionalClause.length === 3) {
            ConditionalClause.splice(ConditionalClause.length - 2, 2);
          }
        } else if (this.gender === 'Female') {
          question.hide = false;
        }
      }
      if (this.processorService.showQuestion(question.DefaultValueConditional, this.questions, this.parent.value, this.medicalConditionsFormGroup.value)) {
        this.medicalConditionsFormGroup.get(QuestionName).enable();
        this.medicalConditionsFormGroup.get(QuestionName).patchValue(question.DefaultValue.replace(/["\\]/g, ''));
      }
      this.defaultValuesChecked.push(QuestionName);
    }

    if (parentShown) {
      // enable console logs for debuggiong all questions (proceed with caution):
      // this.processorService.enableComments(true);
      // enable console logs for debuggiong a specific question:
      // this.processorService.enableComments(QuestionName === 'ExistingPolicyCompany' ? true : false);

      if (ConditionalClause === '0') {
        this.visibleQuestions[QuestionName] = false;
        return false;
      }

      // Populate QuestionTextAlt based on AlternativeQuestionText ConditionalClause
      if (question.QuestionVariations) {
        question.QuestionVariations.forEach(cond => {
          if (this.processorService.analyzeConditionalArray(cond.ConditionalClause, this.questions, this.medicalConditionsFormGroup.value)) {
            question.QuestionTextAlt = cond.AlternativeQuestionText ? cond.AlternativeQuestionText : " ";
            return;
          }
        });
      }

      // Populate OptionAltText based on QuestionOptionVariations ConditionalClause
      if (question.QuestionOptions) {
        question.QuestionOptions.forEach(cond => {
          if (cond.QuestionOptionVariations) {
            cond.QuestionOptionVariations.forEach((variation) => {
              if (this.processorService.analyzeConditionalArray(variation.ConditionalClause, this.questions, this.medicalConditionsFormGroup.value)) {
                cond.OptionAltText = variation.AlternativeQuestionOptionText ? variation.AlternativeQuestionOptionText : " ";
              }
            });
          }
        });
      }
    }

    let show: boolean = parentShown;
    
    if (show && question.ConditionalClause) { 
      show = this.processorService.showConditionalQuestion(question, this.questions, this.medicalConditionsFormGroup.value);
    }

    const field = this.medicalConditionsFormGroup.get(QuestionName);
    if (field) {
      if (show === false) {
        field.reset();
        field.disable();
      } else {
        field.enable();
      }
      field.updateValueAndValidity();
    }

    const menuDataQ = find(this.sectionMenuData.questions, { 'name': question.QuestionName });

    if (question.QuestionName === 'ConditionName') {
      menuDataQ.visible = false;
    } else {
      this.visibleQuestions[question.QuestionName] = show;
      if (menuDataQ) {
        menuDataQ.visible = show;
      }
    }
    
    if (question.ChildQuestions) {
      const questionList = offBlockQuestions ? offBlockQuestions : this.questions;
      forEach(question.ChildQuestions, val => {
        const childQuestion = this.qcs.getQuestion(questionList, val.QuestionName);
        this.showConditionalQuestion(childQuestion, show, questionList);
      });
    }
  }

}